// ------------------------------------------------------------------
// PURE MODULE: canvas_dispersion2.mod.js - V2 - improved render performance. NOTE: Image size needs to be up to 0.8 mega pixels (e.g. 1200x675 px) for optimal performance and to stay away from long tasks at 4x CPU throttling
// ------------------------------------------------------------------
import gsap from "gsap";

export function CanvasDispersion() {
    let instanceConfig = {};
    let proxy = { value: 0 };
    let elementUnits = [];

    return {
        init(customConfig = {}) {
            instanceConfig = { ...CanvasDispersion.defaults, ...customConfig };
            const wrappers = document.querySelectorAll(instanceConfig.targets);

            // =========================================================
            // [UNIVERSAL CORE]: LAZY-BUILD OBSERVER
            // =========================================================
            const buildObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (entry.target._buildAnimation) entry.target._buildAnimation(); 
                    } else {
                        if (entry.target._destroyAnimation) entry.target._destroyAnimation();
                    }
                });
            }, { rootMargin: "800px 0px" });

            elementUnits = Array.from(wrappers).map((wrapper, index) => {
                
                // =========================================================
                // [REPLACEABLE SHELL]: DOM QUERIES & LOCAL STATE
                // =========================================================
                //const img = wrapper.querySelector('.main-img');
                const img = wrapper.querySelector(instanceConfig.imgclass);
                
                if (!img) return () => {}; 

                let isBuilt = false;
                let isInView = false; 
                let isPinnedOverride = false;
                
                let canvas, ctx; 
                let offRed, offBlue, offGreen; // Memory canvases for VRAM caching
                let wasCleared = true; // Dirty flag for the sleep state
                
                const speed = instanceConfig.speeds[index] ?? instanceConfig.speeds[instanceConfig.speeds.length - 1];
                const setImgOpacity = gsap.quickSetter(img, "opacity"); // O(1) DOM writing
                const setImgScale = gsap.quickSetter(img, "scale"); // O(1) DOM writing

                // =========================================================
                // [UNIVERSAL CORE]: STATE SETTERS
                // =========================================================
                wrapper._setVisibility = (visible) => { isInView = visible; };
                wrapper._setOverride = (pinned) => { isPinnedOverride = pinned; };
                
                // =========================================================
                // [REPLACEABLE SHELL]: GARBAGE COLLECTION
                // =========================================================
                wrapper._destroyAnimation = () => {
                    if (!isBuilt) return;
                    
                    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
                    
                    // Nuke all heavy texture memory
                    canvas = ctx = offRed = offBlue = offGreen = null; 
                    setImgOpacity(1); // Restore native image
                    isBuilt = false; 
                };

                // =========================================================
                // [REPLACEABLE SHELL]: THE BUILD LOGIC (ASYNC PRE-RENDERING)
                // =========================================================
                wrapper._buildAnimation = async () => {
                    if (isBuilt) return; 
                    
                    if (!img.complete || img.naturalWidth === 0) {
                        img.addEventListener('load', () => wrapper._buildAnimation(), { once: true });
                        return;
                    }

                    // --- NEW: Asynchronous Image Decoding ---
                    try {
                        if (img.decode) {
                            await img.decode();
                        }
                    } catch (e) {
                        // Fallback: silently proceed if unsupported or image data is corrupted
                    }
                    
                    const dpr = window.devicePixelRatio || 1;
                    // NEW: Force baked layers to 1x resolution (or lower on mobile) to save CPU
                    const bakeDpr = Math.min(1, dpr); 
                    // Aggressive downsample for offscreen memory (calculates 1/16th the pixels)
                    //const bakeDpr = 0.25;
                    const w = img.offsetWidth;
                    const h = img.offsetHeight;

                    const imgRatio = img.naturalWidth / img.naturalHeight;
                    const containerRatio = w / h;
                    let sX = 0, sY = 0, sWidth = img.naturalWidth, sHeight = img.naturalHeight;

                    if (imgRatio > containerRatio) {
                        sWidth = img.naturalHeight * containerRatio; 
                        sX = (img.naturalWidth - sWidth) / 2;
                    } else {
                        sHeight = img.naturalWidth / containerRatio; 
                        sY = (img.naturalHeight - sHeight) / 2;
                    }

                    // 1. Setup the main display canvas (Remains high-res Retina)
                    canvas = document.createElement('canvas');
                    canvas.style.cssText = "position:absolute; top:0; left:0; width:100%; height:100%; z-index:11; pointer-events:none; will-change: opacity, transform;";
                    canvas.width = w * dpr;
                    canvas.height = h * dpr;
                    ctx = canvas.getContext('2d');
                    wrapper.appendChild(canvas);

                    // 2. Helper to bake filters asynchronously using Time-Slicing
                    const bakeChannelAsync = (filterStr) => {
                        return new Promise((resolve) => {
                            // setTimeout 0 yields back to the main thread, breaking up the Long Task
                            setTimeout(() => {
                                const off = document.createElement('canvas');
                                off.width = w * bakeDpr;
                                off.height = h * bakeDpr;
                                const oCtx = off.getContext('2d');
                                oCtx.filter = filterStr; 
                                
                                oCtx.drawImage(img, sX, sY, sWidth, sHeight, 0, 0, w * bakeDpr, h * bakeDpr);
                                resolve(off);
                            }, 0);
                        });
                    };

                    // 3. Chain the bakes so they don't block a single frame
                    const buildLayers = async () => {
                        //offRed = await bakeChannelAsync('blur(6px) brightness(0.6) sepia(1) hue-rotate(-50deg) saturate(10)');
                        //offBlue = await bakeChannelAsync('blur(6px) brightness(0.6) sepia(1) hue-rotate(190deg) saturate(10)');
                        offRed = await bakeChannelAsync('blur(2px) hue-rotate(-50deg) saturate(10)');
                        offBlue = await bakeChannelAsync('blur(2px) hue-rotate(190deg) saturate(10)');
                        offGreen = await bakeChannelAsync('hue-rotate(70deg) saturate(10)');
                        
                        isBuilt = true; 
                        wasCleared = true;
                    };

                    buildLayers();
                };
				
                buildObserver.observe(wrapper);

                // =========================================================
                // [UNIVERSAL CORE / REPLACEABLE SHELL]: THE RENDER LOOP
                // =========================================================
                return (gap) => {
                    if (!isBuilt || !isInView || isPinnedOverride) return; 
                    
                    const drift = gap * speed * instanceConfig.intensity;
                    const absDrift = Math.abs(drift);
                    
                    // Calculate intensity factor (0 to 1)
                    const factor = gsap.utils.clamp(0, 1, absDrift / instanceConfig.dispersionMaxRange);

                    // --- The Sleep State Dirty Flag ---
                    if (factor === 0 && !wasCleared) {
                        ctx.clearRect(0, 0, canvas.width, canvas.height); // Wipe canvas clean
                        setImgOpacity(1); // Unhide crisp native image
                        wasCleared = true;
                        return;
                    } else if (factor === 0) {
                        return; // Costs 0 CPU cycles while resting
                    }

                    wasCleared = false;
                    
                    // 1. Hide the native image 
                    //setImgOpacity(1 - factor);
                    //setImgScale(1 - factor);
					//setImgScale(1 + (factor * 0.2));

                    // 2. Draw the cached layers via GPU (Bypasses CSS completely)
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.globalCompositeOperation = 'screen';
                    ctx.globalAlpha = factor; // Fade the effect intensity in

                    const dpr = window.devicePixelRatio || 1;
                    const redX = factor * -20 * dpr;
                    const redY = factor * -12 * dpr;
                    const blueX = factor * 20 * dpr;
                    const blueY = factor * 12 * dpr;

                    //ctx.drawImage(offGreen, 0, 0);
                    //ctx.drawImage(offRed, redX, redY);
                    //ctx.drawImage(offBlue, blueX, blueY);
					
					// This (forces the 1x memory canvas to stretch across the 2x display canvas):
                    ctx.drawImage(offGreen, 0, 0, canvas.width, canvas.height);
                    ctx.drawImage(offRed, redX, redY, canvas.width, canvas.height);
                    ctx.drawImage(offBlue, blueX, blueY, canvas.width, canvas.height);

                };
            });

            if (window.App?.progress) proxy.value = window.App.progress.value;
        },

        // =========================================================
        // [UNIVERSAL CORE]: THE MASTER MOMENTUM DRIVER
        // =========================================================
        update(currentScroll) { 
            if (elementUnits.length === 0) return;
            
            let diff = currentScroll - proxy.value;
            
            if (Math.abs(diff) < 0.1) {
                proxy.value = currentScroll;
                diff = 0;
            } else {
                proxy.value += diff * instanceConfig.lerp;
            }
            
            if (diff === 0) return; 

            const gap = currentScroll - proxy.value; 

            for (let i = 0; i < elementUnits.length; i++) {
                elementUnits[i](gap);
            }
        }
    };
}

// Default Configuration
CanvasDispersion.defaults = {
    targets: ".dispersion-container",
//    imgclass: ".main-img",
    imgclass: ".dispersion-img",
    speeds: [150, 300, 450],
    lerp: 0.05,
    intensity: 10,
    dispersionMaxRange: 150 
};